<template>
    <BaseDropdownMenu :sections="sections" @select="select" @close-auto-focus="keepDialogFocus">
        <template #trigger>
            <!-- Sized from the text-button tokens (line height + vertical padding + 1px borders) so it is a square matching the CTA beside it. Avoids the lh unit, which iOS 15 webviews lack. -->
            <BaseButton
                ref="trigger"
                theme="neutral"
                icon-only
                class="size-[calc(var(--leading-button)*var(--text-button)+2*var(--spacing-button-y)+2px)] p-0"
                :disabled="disabled"
                :aria-label="t('teamPage.teamOptions', { name: report.name })">
                <i class="bi bi-three-dots flex items-center justify-center" aria-hidden="true" />
            </BaseButton>
        </template>
        <template #item-leave="{ item }">
            <span class="flex flex-col">
                {{ item.label }}
                <span v-if="!report.can_leave" class="details text-ink-muted">{{ t("teamPage.leadLeaveHelp") }}</span>
            </span>
        </template>
    </BaseDropdownMenu>
    <LeaveTeamDialog
        v-if="leaveUrl"
        v-model:open="leaveOpen"
        :source="source"
        :team-name="report.name"
        :leave-url="leaveUrl"
        :show-trigger="false"
        :return-focus-to="trigger"
        @left="emit('left')" />
    <ConfirmationDialog
        v-model:open="deleteOpen"
        :title="t('teamPage.delete.title', { name: report.name })"
        :description="deleteDescription"
        :confirm-label="deleting ? t('teamPage.delete.pending') : t('teamPage.delete.action')"
        confirm-theme="danger"
        :cancel-label="t('teamPerspective.cancel')"
        :confirm-disabled="deleting"
        :cancel-disabled="deleting"
        :close-on-confirm="false"
        :return-focus-to="trigger"
        dialog-content-class="fs-mask"
        @confirm="deleteTeam">
        <template #content>
            <p class="body text-ink">
                <strong>{{ t("teamPage.delete.consequence") }}</strong>
                {{ t("teamPage.delete.description") }}
                <strong>{{ t("teamPage.delete.irreversible") }}</strong>
            </p>
            <BaseNotification v-if="deleteError" theme="error" :text="deleteError" hide-dismiss />
        </template>
    </ConfirmationDialog>
</template>
<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { TeamPerspectiveReport } from "~vue/api/generated/team-perspective";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import LeaveTeamDialog from "~vue/Connections/LeaveTeamDialog.vue";
import { TEAM_INTERACTION_SOURCE, teamInteractionRequest, type TeamInteractionSource } from "~vue/Teams/teamInteraction";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const {
    source = TEAM_INTERACTION_SOURCE.TEAM_PAGE,
    report,
    leaveUrl,
    deleteUrl,
    disabled = false,
} = defineProps<{ source?: TeamInteractionSource; report: TeamPerspectiveReport; leaveUrl: string | null; deleteUrl: string | null; disabled?: boolean }>();
const emit = defineEmits<{ left: []; deleted: [] }>();
const { t } = useI18n();
// The dialogs have no trigger of their own, so closing them hands focus back to the "…".
const trigger = useTemplateRef<InstanceType<typeof BaseButton>>("trigger");
const leaveOpen = ref(false);
const deleteOpen = ref(false);
const deleting = ref(false);
const deleteError = ref("");
const deleteDescription = computed(() => [t("teamPage.delete.consequence"), t("teamPage.delete.description"), t("teamPage.delete.irreversible")].join(" "));
const sections = computed<DropdownMenuSection[]>(() => {
    const items: DropdownMenuItem[] = [];
    if (leaveUrl) {
        // A lead keeps the item, unavailable but still focusable, so keyboard users can reach the reason shown under it.
        items.push({ id: "leave", type: "button", label: t("teamPage.leaveThisTeam"), ariaDisabled: !report.can_leave });
    }
    if (deleteUrl && report.can_manage) {
        items.push({ id: "delete", type: "button", label: t("teamPage.deleteTheTeam"), theme: "danger" });
    }
    return [{ id: "team", items }];
});
function select(item: DropdownMenuItem) {
    if (item.id === "leave") {
        leaveOpen.value = true;
    } else if (item.id === "delete") {
        deleteError.value = "";
        deleteOpen.value = true;
        logUserInteraction(AmplitudeEventNames.TEAM_DELETION_STARTED, { source, team_id: report.id });
    }
}
// The dialog takes focus as the menu closes; returning it to the trigger would pull it back out.
function keepDialogFocus(event: Event) {
    if (leaveOpen.value || deleteOpen.value) {
        event.preventDefault();
    }
}
async function deleteTeam() {
    if (!deleteUrl || !report.can_manage || deleting.value) {
        return;
    }
    deleting.value = true;
    deleteError.value = "";
    try {
        const { error: failure, statusCode } = await useFetch(deleteUrl, teamInteractionRequest(source)).delete();
        if (failure.value || statusCode.value !== 204) {
            throw new Error("Deletion failed");
        }
        deleteOpen.value = false;
        emit("deleted");
    } catch {
        deleteError.value = t("teamPage.delete.error");
    } finally {
        deleting.value = false;
    }
}
</script>
